import { createContext } from "react";

export function getDateAsIndex(date: Date, displayStartDate: Date): number {
    const startDate = new Date(displayStartDate);
    const daysSinceStart = Math.round((date.setHours(0,0,0) - startDate.setHours(0,0,0)) / (1000 * 60 * 60 * 24));
    return daysSinceStart;
}

export function getWeekDateRange(weekIndex: number, displayStartDate: Date): { startDate: Date, finishDate: Date } {
    // Ensure startDate is the Monday of the week containing displayStartDate
    const startDate = new Date(displayStartDate);
    const dayOfWeek = startDate.getDay(); // 0 (Sun) - 6 (Sat)
    const diffToMonday = (dayOfWeek === 0 ? -6 : 1) - dayOfWeek;
    startDate.setDate(startDate.getDate() + diffToMonday + (weekIndex * 7));
    const finishDate = new Date(startDate);
    finishDate.setDate(finishDate.getDate() + 6); // End of the week (Sunday)
    return { startDate, finishDate };
}

export function getMonthDateRange(monthIndex: number, displayStartDate: Date): { startDate: Date, finishDate: Date } {
    const startDate = new Date(displayStartDate);
    startDate.setDate(1); // Set to the first day of the month
    startDate.setMonth(startDate.getMonth() + monthIndex);
    
    const finishDate = new Date(startDate);
    finishDate.setMonth(finishDate.getMonth() + 1); // Move to the next month
    finishDate.setDate(0); // Set to the last day of the month
    return { startDate, finishDate };
}

export function getScaleRangeIncludesDate(displayStartDate: Date, index: number, scale: GanttScale, date: Date): boolean {
    const startDate = new Date(displayStartDate);
    let scaleStartDate: Date;
    let scaleFinishDate: Date;

    switch (scale) {
        case "day":
            scaleStartDate = new Date(startDate);
            scaleStartDate.setDate(scaleStartDate.getDate() + index);
            scaleFinishDate = new Date(scaleStartDate);
            scaleFinishDate.setHours(23, 59, 59, 999); // End of the day
            break;
        case "week":
            const weekRange = getWeekDateRange(index, startDate);
            scaleStartDate = weekRange.startDate;
            scaleFinishDate = weekRange.finishDate;
            break;
        case "month":
            const monthRange = getMonthDateRange(index, startDate);
            scaleStartDate = monthRange.startDate;
            scaleFinishDate = monthRange.finishDate;
            break;
        case "year":
            scaleStartDate = new Date(startDate);
            scaleStartDate.setFullYear(scaleStartDate.getFullYear() + index);
            scaleStartDate.setMonth(0, 1); // January 1st
            scaleFinishDate = new Date(scaleStartDate);
            scaleFinishDate.setFullYear(scaleFinishDate.getFullYear() + 1);
            scaleFinishDate.setMonth(0, 0); // Last day of the previous year
            break;
        default:
            return false; // Invalid scale
    }

    return date >= scaleStartDate && date <= scaleFinishDate;
}

export function getPositionInRange(rangeStart: Date, rangeFinish: Date, date: Date): number {
    const rangeInDays = Math.round((new Date(rangeFinish).setHours(0, 0, 0) - new Date(rangeStart).setHours(0, 0, 0)) / (1000 * 60 * 60 * 24)) + 1;
    const position = Math.round((new Date(date).setHours(0, 0, 0) - new Date(rangeStart).setHours(0, 0, 0)) / (1000 * 60 * 60 * 24));
    
    return (position) / rangeInDays * 100;
}

export function getPositionFromEndOfRange(rangeStart: Date, rangeFinish: Date, date: Date): number {
    const rangeInDays = Math.round((new Date(rangeFinish).setHours(0, 0, 0) - new Date(rangeStart).setHours(0, 0, 0)) / (1000 * 60 * 60 * 24)) + 1;
    const position = Math.round((new Date(date).setHours(0, 0, 0) - new Date(rangeStart).setHours(0, 0, 0)) / (1000 * 60 * 60 * 24)) + 1; // add 1 as position is 0 based
    return (rangeInDays - position) / rangeInDays * 100;
}

export function getIsInRange(date: Date, rangeStart: Date, rangeFinish: Date): boolean {
    const start = new Date(rangeStart).setHours(0, 0, 0, 0);
    const finish = new Date(rangeFinish).setHours(0, 0, 0, 0);
    const dateTimestamp = new Date(date).setHours(0, 0, 0, 0);
    return dateTimestamp >= start && dateTimestamp <= finish;
}

export function getRangeOverlap(rangeStart: Date, rangeFinish: Date, lineStart: Date, lineFinish: Date): boolean {
    const start = new Date(rangeStart).setHours(0, 0, 0, 0);
    const finish = new Date(rangeFinish).setHours(0, 0, 0, 0);
    const lineStartDate = new Date(lineStart).setHours(0, 0, 0, 0);
    const lineFinishDate = new Date(lineFinish).setHours(0, 0, 0, 0);
    return (lineStartDate < finish && lineFinishDate > start);
}

// export function getRangeOverlapInDays(rangeStart: Date, rangeFinish: Date, lineStart: Date, lineFinish: Date): number {
//     const start = new Date(rangeStart);
//     const finish = new Date(rangeFinish);
//     const lineStartDate = new Date(lineStart);
//     const lineFinishDate = new Date(lineFinish);
//     if (!getRangeOverlap(rangeStart, rangeFinish, lineStart, lineFinish)) {
//         return 0;
//     }
//     const overlapStart = lineStartDate > start ? lineStartDate : start;
//     const overlapFinish = lineFinishDate < finish ? lineFinishDate : finish;
//     const overlapInDays = Math.round((overlapFinish.setHours(0, 0, 0) - overlapStart.setHours(0, 0, 0)) / (1000 * 60 * 60 * 24)) - 1;
//     return overlapInDays > 0 ? overlapInDays : 0;
// }

export function getIndexInRange(index: number, startDate: Date, finishDate : Date, displayStartDate : Date): boolean {
    const startIndex = getDateAsIndex(new Date(startDate), new Date(displayStartDate));
    const finishIndex = getDateAsIndex(new Date(finishDate), new Date(displayStartDate));
    return index >= startIndex && index <= finishIndex;
}

export function getIndexAsDate(index : number, displayStartDate: Date): Date {
    const startDate = new Date(displayStartDate);
    startDate.setDate(startDate.getDate() + index);
    return startDate;
}

export type GanttScale = "day" | "week" | "month" | "year";

interface GanttContextProps {
    setDisplayLength: (length: number) => void
    displayLength: number
    setDisplayStartDate: (date: Date) => void
    displayStartDate: Date
    setGanttScale: (scale: GanttScale) => void
    ganttScale: GanttScale
    projectStartDate: Date
    projectEndDate: Date
    scaleToSelected: () => void
}

export const GanttContext = createContext<GanttContextProps>({
    setDisplayLength: () => {},
    displayLength: 0,
    setDisplayStartDate: () => {},
    displayStartDate: new Date(),
    setGanttScale: () => {},
    ganttScale: "day",
    projectStartDate: new Date(),
    projectEndDate: new Date(),
    scaleToSelected: () => {},
});